import React from 'react';
import { Check, Truck, Shield, Award } from 'lucide-react';

const AboutPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="about-page-title">
            About <span className="text-blue-300">HVAC Best Supply</span>
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
            Your trusted partner for premium refrigerants and HVAC supplies
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Who We Are</h2>
            <div className="text-gray-700 space-y-4 text-lg">
              <p>
                HVAC Best Supply is a leading national refrigerant wholesaler and supplier based in Downey, California, serving HVAC professionals across the United States. We specialize in providing high-quality, virgin refrigerants at competitive prices with fast, reliable shipping.
              </p>
              <p>
                Our mission is to be the go-to source for HVAC contractors and technicians who need reliable refrigerant supplies without the hassle. We understand that time is money in your business, which is why we've built our entire operation around speed, quality, and customer satisfaction.
              </p>
              <p>
                With our strategically located fulfillment center in Downey, California, fully stocked with both common and specialized refrigerants, we can serve all lower 48 states with fast and free shipping. Whether you need a single cylinder or a full pallet, we've got you covered.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Shield className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">100% Virgin Quality</h3>
              <p className="text-gray-600">
                All our refrigerants are brand new, 100% virgin products that meet or exceed ARI-700 standards. We never sell reclaimed or contaminated refrigerants.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Truck className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Fast & Free Shipping</h3>
              <p className="text-gray-600">
                Enjoy free shipping on all orders with fast delivery to your location. Most orders ship within 24 hours and arrive in 3-4 business days.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Award className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Volume Discounts</h3>
              <p className="text-gray-600">
                Save more when you buy more with our competitive volume pricing. Perfect for contractors who need to stock up for multiple jobs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <Check className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold mb-1">Wide Product Selection</h4>
                  <p className="text-gray-600">From R22 to the latest eco-friendly alternatives like R454B and R448A</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Check className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold mb-1">Competitive Pricing</h4>
                  <p className="text-gray-600">Volume discounts and regular promotions to save you money</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Check className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold mb-1">Expert Support</h4>
                  <p className="text-gray-600">Knowledgeable team ready to help with product selection and orders</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Check className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold mb-1">Secure Packaging</h4>
                  <p className="text-gray-600">Double-packed to ensure safe delivery without leakage</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Check className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold mb-1">Quick Response</h4>
                  <p className="text-gray-600">Fast quote turnaround and responsive customer service</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Check className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold mb-1">Nationwide Coverage</h4>
                  <p className="text-gray-600">Serving all 48 contiguous states with reliable delivery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Browse our products or contact us for a custom quote
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="/products"
              className="bg-white text-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              View Products
            </a>
            <a
              href="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;